import { useState, useCallback, useRef, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useAppSelector } from "../store";
import type { SendAIChatMessagePayload } from "@nicsmith/shared_types";

interface StreamState {
  isStreaming: boolean;
  streamedContent: string;
  error: string | null;
  errorCode: string | null;
}

export function useAIChatStream(moduleId: number) {
  const [state, setState] = useState<StreamState>({
    isStreaming: false,
    streamedContent: "",
    error: null,
    errorCode: null,
  });

  const abortRef = useRef<AbortController | null>(null);
  const idToken = useAppSelector((s) => s.session.idToken);
  const idTokenRef = useRef(idToken);
  useEffect(() => {
    idTokenRef.current = idToken;
  }, [idToken]);

  const { isImpersonating, targetUid } = useAppSelector((s) => s.impersonation);
  const impersonationRef = useRef({ isImpersonating, targetUid });
  useEffect(() => {
    impersonationRef.current = { isImpersonating, targetUid };
  }, [isImpersonating, targetUid]);

  const sendMessage = useCallback(
    async (
      payload: SendAIChatMessagePayload,
      onDone?: (data: { message_id: string; total_tokens: number }) => void
    ) => {
      // Abort any existing stream
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setState({
        isStreaming: true,
        streamedContent: "",
        error: null,
        errorCode: null,
      });

      try {
        const apiUrl = import.meta.env.VITE_API_URL;
        const response = await fetch(
          `${apiUrl}/api/v1/ai/chat/${moduleId}/message`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idTokenRef.current}`,
              ...(impersonationRef.current.isImpersonating && impersonationRef.current.targetUid
                ? { "X-Impersonate-Uid": impersonationRef.current.targetUid }
                : {}),
            },
            body: JSON.stringify(payload),
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          const errMsg = errorData?.message || errorData?.error?.message || `HTTP ${response.status}`;
          throw new Error(errMsg);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No response body");

        const decoder = new TextDecoder();
        let buffer = "";
        let accumulated = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete last line

          let eventType = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              const data = line.slice(6);
              try {
                const parsed = JSON.parse(data);

                if (eventType === "token") {
                  accumulated += parsed.content;
                  setState((prev) => ({
                    ...prev,
                    streamedContent: accumulated,
                  }));
                } else if (eventType === "done") {
                  setState((prev) => ({
                    ...prev,
                    isStreaming: false,
                  }));
                  onDone?.(parsed);
                } else if (eventType === "error") {
                  setState({
                    isStreaming: false,
                    streamedContent: accumulated,
                    error: parsed.error,
                    errorCode: parsed.code || null,
                  });
                }
              } catch {
                // Ignore malformed JSON
              }
            }
          }
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        Sentry.captureException(err);
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: err instanceof Error ? err.message : "Failed to send message",
        }));
      }
    },
    [moduleId]
  );

  const abort = useCallback(() => {
    abortRef.current?.abort();
    setState((prev) => ({ ...prev, isStreaming: false }));
  }, []);

  return {
    ...state,
    sendMessage,
    abort,
  };
}
